/**
 * Permission codes the Procurement (P2P) screens declare an interest in.
 *
 * These are the dot-strings `internal/procurement/permissions.go` registers with
 * the RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. Naming them
 * once here means a renamed permission is a compile error rather than a
 * silently invisible button.
 *
 * **The PO trio is a declared segregation-of-duties triangle.** `permissions.go`
 * says it in as many words: the person who raises a purchase order should not be
 * the one who approves it, and the approver should not be the one who confirms
 * that what arrived is what was ordered. So Save, Submit, Approve, Issue,
 * Confirm, Receive and Match are separately permissioned actions on these
 * screens, and **no action is ever inferred from another** — a buyer who may
 * only raise requisitions sees Save and Submit and nothing else, rather than a
 * row of disabled buttons implying they are one promotion away from receiving
 * their own goods.
 *
 * Two codes here are not procurement's own and are here because these screens
 * cannot do their job without them:
 *
 *  - `ap.invoice.read` — the three-way match compares an invoice this module
 *    does not own; without it the match screen can show the PO and the receipt
 *    but not the document they are being matched against.
 *  - `budget.check` — the live budget indicator §8.9 asks for on a requisition
 *    line is `internal/budget`'s answer, not procurement's (§8.7).
 */

export const PROCUREMENT_PERMISSIONS = {
  prRead: 'procurement.pr.read',
  prCreate: 'procurement.pr.create',
  prUpdate: 'procurement.pr.update',
  prDelete: 'procurement.pr.delete',
  prSubmit: 'procurement.pr.submit',
  prApprove: 'procurement.pr.approve',
  prCancel: 'procurement.pr.cancel',

  rfqRead: 'procurement.rfq.read',
  rfqCreate: 'procurement.rfq.create',
  rfqIssue: 'procurement.rfq.issue',
  /** Record a supplier's quote. A buyer keying in a phoned-in bid today; the portal (M10) later. */
  rfqQuote: 'procurement.rfq.quote',
  rfqAward: 'procurement.rfq.award',

  poRead: 'procurement.po.read',
  poCreate: 'procurement.po.create',
  poUpdate: 'procurement.po.update',
  poSubmit: 'procurement.po.submit',
  poApprove: 'procurement.po.approve',
  poAmend: 'procurement.po.amend',
  poIssue: 'procurement.po.issue',
  poConfirm: 'procurement.po.confirm',
  poClose: 'procurement.po.close',
  poCancel: 'procurement.po.cancel',

  grnRead: 'procurement.grn.read',
  grnCreate: 'procurement.grn.create',
  grnReverse: 'procurement.grn.reverse',

  matchRead: 'procurement.match.read',
  matchCreate: 'procurement.match.create',
  matchResolve: 'procurement.match.resolve',

  /* ------------------------------------------------------------- C7 (§6.1) */

  /** Releasing or rejecting a quarantined receipt line. */
  grnInspect: 'procurement.grn.inspect',

  rtsCreate: 'procurement.rts.create',
  rtsApprove: 'procurement.rts.approve',

  /**
   * A **new** read code, granted to every role that already holds
   * `procurement.po.read`.
   *
   * It exists because `internal/ap` owns `ap.vendor.read`, and that code gates
   * the vendor's **AES-GCM-encrypted bank details**. A procurement officer
   * reading a supplier's qualification should not thereby be able to read where
   * that supplier is paid. Standing rule 9 applied before the bug: ask what a
   * permission protects, not who holds it.
   */
  supplierRead: 'procurement.supplier.read',
  /** Read requisition control policies and their change requests (B63). */
  categoryPolicyRead: 'procurement.category_policy.read',
  /** SoD partner of `poApprove`: deciding a supplier may be used, and committing money to them. */
  supplierQualify: 'procurement.supplier.qualify',
  supplierDocument: 'procurement.supplier.document',
  /** Step-up MFA. Recording a screening and reviewing a potential match. */
  supplierScreen: 'procurement.supplier.screen',

  contractRead: 'procurement.contract.read',
  /** SoD partner of `contractApprove`. */
  contractManage: 'procurement.contract.manage',
  /** SoD partner of `contractManage`. */
  contractApprove: 'procurement.contract.approve',
  rebateManage: 'procurement.rebate.manage',

  spendRead: 'procurement.spend.read',

  /**
   * Resolving a routed match exception.
   *
   * Distinct from `matchResolve`, which **accepts a tolerance breach**. An
   * exception has an owner and is investigated; a breach has an approver and is
   * accepted. They are two acts and the inbox that shows them is not the
   * approvals inbox.
   */
  matchException: 'procurement.match.exception',

  poSchedule: 'procurement.po.schedule',

  /** Step-up MFA. One of the two people who must open a sealed RFQ. */
  rfqOpen: 'procurement.rfq.open',
  /** SoD partner of `rfqAward`. */
  rfqEvaluate: 'procurement.rfq.evaluate',

  scorecardRead: 'procurement.scorecard.read',
  /** The GR-IR aging report and its reconciliation against the ledger. */
  reportRead: 'procurement.report.read',
  /**
   * Read **and** write the GR-IR account and the tolerance policy.
   *
   * The server guards `GET /procurement-settings` with the same code as the PUT,
   * so a receiving clerk who may not change a tolerance also may not read one.
   * Every screen that would like to show a tolerance before somebody breaches it
   * therefore checks this first and says plainly when it cannot.
   */
  settingsManage: 'procurement.settings.manage',

  /** Vendors are `internal/ap`'s (§8.3): suppliers, RFQ invitees, PO counterparties. */
  vendorRead: 'ap.vendor.read',
  /** The invoice side of the three-way match, and the AP hop of the linked-docs chain. */
  invoiceRead: 'ap.invoice.read',
  /** Posting a matched invoice is AP's act, and it is where `ERR_THREE_WAY_MATCH` is raised. */
  invoicePost: 'ap.invoice.post',
  /** The PAY hop of the chain — payment runs are `internal/ap`'s (§8.3). */
  paymentRead: 'ap.payment.read',

  /** §8.7's budget control, consulted live while a requisition line is typed. */
  budgetCheck: 'budget.check',

  /** The chart of accounts, to code a requisition or order line. */
  accountRead: 'org.account.read',
  /** Companies and their dimension trees, to scope a document. */
  companyRead: 'org.company.read',
  unitRead: 'org.unit.read',

  /** Catalogue items, warehouses and bins — `internal/inventory`'s (§8.10). */
  itemRead: 'inventory.item.read',
  warehouseRead: 'inventory.warehouse.read',
} as const

export type ProcurementPermission =
  (typeof PROCUREMENT_PERMISSIONS)[keyof typeof PROCUREMENT_PERMISSIONS]
